
import { useState } from 'react';
import { toast } from "sonner";
import { useContactPhotoMutation } from './useContactPhotoMutation';
import { 
  IMAGE_PATHS, 
  FILE_NAMES,
  validateImageFile,
  validateImageUrl,
  uploadStorageFile,
  uploadFromUrl,
} from '@/utils/image-storage';
import { logger } from "@/services/LoggingService";

const COMPONENT = 'ContactPhoto';
const MAX_IMAGE_SIZE = 800; // Maximum dimension in pixels

interface Contact {
  id: string;
  full_name: string | null;
  title: string | null;
  work_email: string | null;
  linkedin_profile?: string | null;
  phone_number?: string | null;
  key_contact: boolean;
  avatar_url?: string | null;
}

async function optimizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > MAX_IMAGE_SIZE || height > MAX_IMAGE_SIZE) {
        if (width > height) {
          height = Math.round(height * MAX_IMAGE_SIZE / width);
          width = MAX_IMAGE_SIZE;
        } else {
          width = Math.round(width * MAX_IMAGE_SIZE / height);
          height = MAX_IMAGE_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use better quality scaling
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to WebP format for better compression
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        
        const optimizedFile = new File([blob], file.name, {
          type: 'image/webp',
          lastModified: Date.now(),
        });
        
        resolve(optimizedFile);
      }, 'image/webp', 0.85); // 85% quality for good balance
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

export const useContactPhoto = (contact: Contact, fundId: string, onComplete: (updatedContact: Contact) => void) => {
  const [isUploading, setIsUploading] = useState(false);
  const [avatarUrlInput, setAvatarUrlInput] = useState(contact.avatar_url || '');
  const { mutateAsync: updatePhoto } = useContactPhotoMutation(contact.id, fundId);

  const handleFileUpload = async (file: File) => {
    if (!file) {
      logger.warn(COMPONENT, 'No file selected');
      toast.error('No file selected');
      return false;
    }

    try {
      logger.info(COMPONENT, 'Starting file upload', {
        contactId: contact.id,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      setIsUploading(true);
      validateImageFile(file);

      // Optimize image before upload
      const optimizedFile = await optimizeImage(file);
      logger.info(COMPONENT, 'Image optimized', {
        originalSize: file.size,
        optimizedSize: optimizedFile.size
      });

      const publicUrl = await uploadStorageFile(
        IMAGE_PATHS.CONTACT(contact.id),
        FILE_NAMES.CONTACT_AVATAR,
        optimizedFile,
        { upsert: true }
      );
      
      const updatedContact = await updatePhoto(publicUrl);
      onComplete(updatedContact);
      return true;
      
    } catch (error: any) {
      logger.error(COMPONENT, 'File upload failed', {
        error: error.message,
        details: error,
        contactId: contact.id
      });
      toast.error(error.message || "Failed to upload photo");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlUpdate = async () => {
    try {
      validateImageUrl(avatarUrlInput);
      setIsUploading(true);
      
      const publicUrl = await uploadFromUrl(
        IMAGE_PATHS.CONTACT(contact.id),
        FILE_NAMES.CONTACT_AVATAR,
        avatarUrlInput,
        { upsert: true }
      );

      const updatedContact = await updatePhoto(publicUrl);
      onComplete(updatedContact);
      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'URL upload failed', {
        error: error.message,
        details: error,
        contactId: contact.id
      });
      toast.error(error.message || "Failed to update photo URL");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = async () => {
    try {
      setIsUploading(true);
      const updatedContact = await updatePhoto(null);
      onComplete(updatedContact);
      return true;
    } catch (error: any) {
      logger.error(COMPONENT, 'Photo removal failed', {
        error: error.message,
        details: error,
        contactId: contact.id
      });
      toast.error(error.message || "Failed to remove photo");
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    avatarUrlInput,
    setAvatarUrlInput,
    handleFileUpload,
    handleUrlUpdate,
    removePhoto
  };
};
